import { BANNER_TARGET_KIND_LABELS } from "./banner-link-builder";

import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useGetAllCountries } from "@/hooks/api/country/useGetAllCountries";
import { useGetProductSummary } from "@/hooks/api/product/useGetProductSummary";
import { Banner } from "@/types";
import {
  BannerTarget,
  ProductTarget,
  ProductsTarget,
  parseBannerLink,
  parseHttpUrl,
} from "@/utils/banner-link";

interface NamedEntity {
  id: string;
  name: string;
}

const findName = (entities: NamedEntity[] | undefined, id: string): string =>
  entities?.find((entity) => entity.id === id)?.name ?? id;

const DestinationLabel = ({ label }: { label: string }) => (
  <span className="block w-full truncate font-medium" title={label}>
    {label}
  </span>
);

const describeTarget = (
  target: Exclude<BannerTarget, ProductTarget | ProductsTarget>,
): string => {
  switch (target.kind) {
    case "search":
      return `Search: ${target.searchTerm}`;
    case "website":
      return `Website: ${parseHttpUrl(target.url)?.hostname ?? target.url}`;
    case "custom":
      return `Custom: ${target.link}`;
    default:
      return BANNER_TARGET_KIND_LABELS[target.kind];
  }
};

const ProductDestinationLabel = ({ target }: { target: ProductTarget }) => {
  const { data: product } = useGetProductSummary(target.productId);

  return (
    <DestinationLabel label={`Product: ${product?.name ?? target.productId}`} />
  );
};

const ProductsDestinationLabel = ({ target }: { target: ProductsTarget }) => {
  const { data: categories } = useGetAllCategories();
  const { data: brands } = useGetAllBrands();
  const { data: countries } = useGetAllCountries();

  const subCategories = categories?.flatMap(
    (category) => category.subCategories,
  );
  const filterNames = [
    target.categoryId && findName(categories, target.categoryId),
    target.subCategoryId && findName(subCategories, target.subCategoryId),
    target.brandId && findName(brands, target.brandId),
    target.countryId && findName(countries, target.countryId),
  ].filter(Boolean);

  return (
    <DestinationLabel
      label={`Products: ${filterNames.length ? filterNames.join(" · ") : "All"}`}
    />
  );
};

/** The promo code a banner applies when tapped, or an empty string. */
export const getBannerPromoCode = (banner: Banner): string => {
  const target = parseBannerLink(banner.link, banner.linkType);

  return "promoCode" in target ? target.promoCode : "";
};

export const BannerDestination = ({ banner }: { banner: Banner }) => {
  const target = parseBannerLink(banner.link, banner.linkType);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      {target.kind === "product" ? (
        <ProductDestinationLabel target={target} />
      ) : target.kind === "products" ? (
        <ProductsDestinationLabel target={target} />
      ) : (
        <DestinationLabel label={describeTarget(target)} />
      )}
      {target.kind !== "custom" && (
        <span
          className="text-muted block w-full truncate font-mono text-xs"
          title={banner.link}
        >
          {banner.link}
        </span>
      )}
    </div>
  );
};
